import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, message, InputNumber, Table, Input, Alert } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useGetMatClntWhInventory, useGetMatClntWarehouses, useGetMatClntIssueRequests, useGetVwMatClntRequestItemOutstanding } from '../../../hooks/genericHooks';
import type { VwMatClntWhInventory, MatWarehouses, MatClntIssueRequest, VwMatClntRequestItemOutstanding } from '../../../types';
import { insertData, getNextSequenceValue, updateData } from '../../../services/api';
import { useAuth } from '../../../components/common/security/AuthContext';
import PersonnelSelect from '../../../components/modals/PersonnelSelect';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

export default function MatClntTxnIssue() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: invData = [], refetch: refetchInventory } = useGetMatClntWhInventory() as any;
  const { data: whData = [] } = useGetMatClntWarehouses() as any;
  const { data: reqHdrData = [], refetch: refetchReqHeaders } = useGetMatClntIssueRequests() as any;
  const { data: reqOutData = [], refetch: refetchOutstanding } = useGetVwMatClntRequestItemOutstanding() as any;

  const inventory: VwMatClntWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const reqHeaders: MatClntIssueRequest[] = Array.isArray(reqHdrData) ? reqHdrData : (reqHdrData?.data ?? []);
  const { user: currentUser } = useAuth();

  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  const [selectedRefNo, setSelectedRefNo] = useState<string | undefined>(undefined);
  const [issueDate, setIssueDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [fromWarehouseId, setFromWarehouseId] = useState<number | undefined>(undefined);

  // Personnel selection state
  const [personnelOpen, setPersonnelOpen] = useState(false);
  const [personnelDisplay, setPersonnelDisplay] = useState<string>('');
  const [personnelCode, setPersonnelCode] = useState<string | null>(null);

  useEffect(() => {
    if (typeof refetchReqHeaders === 'function') {
      refetchReqHeaders();
    }
    if (typeof refetchOutstanding === 'function') {
      refetchOutstanding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      const st: any = (location as any)?.state || {};
      if (st?.ref_no) setSelectedRefNo(String(st.ref_no));
      if (st?.from_warehouse_id != null) setFromWarehouseId(Number(st.from_warehouse_id));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestOptions = useMemo(() => {
    if (!fromWarehouseId) return [] as { value: string; label: string }[];
    const allOut: VwMatClntRequestItemOutstanding[] = Array.isArray(reqOutData) ? (reqOutData as any) : (reqOutData?.data ?? []);
    const allowedRefs = new Set<string>();
    (allOut || []).forEach(r => {
      if (Number(r.requested_wh_id) === Number(fromWarehouseId) && r.ref_no) {
        allowedRefs.add(String(r.ref_no).trim());
      }
    });
    return reqHeaders
      .filter(h => (h.status === 'approved' || h.status === 'partially_issued') && allowedRefs.has(String(h.ref_no).trim()))
      .map(h => ({ value: h.ref_no, label: `${h.ref_no} - ${h.requester_name || h.requester || ''}` }));
  }, [reqHdrData, reqOutData, fromWarehouseId]);

  useEffect(() => {
    if (!selectedRefNo) return;
    const ok = requestOptions.some(opt => String(opt.value) === String(selectedRefNo));
    if (!ok) setSelectedRefNo(undefined);
  }, [fromWarehouseId, requestOptions]);

  const outstandingItems = useMemo(() => {
    if (!selectedRefNo) return [] as VwMatClntRequestItemOutstanding[];
    const sel = String(selectedRefNo).trim();
    const all: VwMatClntRequestItemOutstanding[] = Array.isArray(reqOutData) ? (reqOutData as any) : (reqOutData?.data ?? []);
    return all.filter(r => String(r.ref_no || '').trim() === sel);
  }, [reqOutData, selectedRefNo]);

  const visibleItems = useMemo(() => {
    if (!fromWarehouseId) return [] as VwMatClntRequestItemOutstanding[];
    return (outstandingItems || []).filter(r => Number(r.requested_wh_id) === Number(fromWarehouseId));
  }, [outstandingItems, fromWarehouseId]);

  const requestWarehouses = useMemo(() => {
    const s = new Set<number>();
    (outstandingItems || []).forEach(r => { if (r.requested_wh_id != null) s.add(Number(r.requested_wh_id)); });
    return s;
  }, [outstandingItems]);

  const isOwner = useMemo(() => {
    if (!fromWarehouseId || !currentUser) return false;
    const wh = (warehouses || []).find(w => Number(w.id) === Number(fromWarehouseId));
    const owners: string[] = Array.isArray(wh?.owners) ? (wh?.owners as string[]) : [];
    return owners.includes(String(currentUser));
  }, [fromWarehouseId, currentUser, warehouses]);
  const canTransact = Boolean(fromWarehouseId && currentUser && isOwner);

  const ownedWhSet = useMemo(() => {
    const s = new Set<number>();
    (warehouses || []).forEach(w => {
      const owners: string[] = Array.isArray(w?.owners) ? (w.owners as string[]) : [];
      if (currentUser && owners.includes(String(currentUser))) s.add(Number(w.id));
    });
    return s;
  }, [warehouses, currentUser]);

  const allowedWhSet = useMemo(() => {
    const s = new Set<number>();
    const all: VwMatClntRequestItemOutstanding[] = Array.isArray(reqOutData) ? (reqOutData as any) : (reqOutData?.data ?? []);
    (all || []).forEach(r => { if (r.requested_wh_id != null && Number(r.outstanding_qty || 0) > 0) s.add(Number(r.requested_wh_id)); });
    return s;
  }, [reqOutData]);

  const fromWarehouseOptions = useMemo(() => {
    return (warehouses || [])
      .filter(w => allowedWhSet.has(Number(w.id)))
      .map(w => ({ value: w.id, label: w.name, disabled: !ownedWhSet.has(Number(w.id)) }));
  }, [warehouses, allowedWhSet, ownedWhSet]);

  useEffect(() => {
    if (fromWarehouseId != null && (!allowedWhSet.has(Number(fromWarehouseId)) || !ownedWhSet.has(Number(fromWarehouseId)))) {
      setFromWarehouseId(undefined);
    }
  }, [allowedWhSet, ownedWhSet]);

  useEffect(() => {
    if (selectedRefNo && requestWarehouses.size === 1) {
      const onlyWh = Array.from(requestWarehouses)[0];
      if (onlyWh != null && fromWarehouseId !== onlyWh) setFromWarehouseId(onlyWh);
    }
  }, [selectedRefNo, requestWarehouses]);

  const [issueQtyByItem, setIssueQtyByItem] = useState<Record<number, number>>({});

  const fulfillColumns: ColumnsType<VwMatClntRequestItemOutstanding> = useMemo(() => [
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 160 },
    { title: 'Description', key: 'description', ellipsis: true, render: (_, row) => {
      const size = row.size_text ? ` (${row.size_text})` : '';
      return `${row.description || ''}${size}`;
    } },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70 },
    { title: 'Requested', dataIndex: 'requested_qty', key: 'requested_qty', width: 110, align: 'right' },
    { title: 'Issued', dataIndex: 'issued_qty', key: 'issued_qty', width: 100, align: 'right' },
    { title: 'Outstanding', dataIndex: 'outstanding_qty', key: 'outstanding_qty', width: 120, align: 'right' },
    {
      title: 'Warehouse', key: 'warehouse', width: 220,
      render: () => (
        <span>{fromWarehouseId != null ? (whMap.get(Number(fromWarehouseId)) || String(fromWarehouseId)) : '-'}</span>
      )
    },
    {
      title: 'On-hand', key: 'onhand', width: 100, align: 'right',
      render: (_, row) => {
        const wh = fromWarehouseId;
        const inv = (wh == null) ? undefined : (inventory || []).find(x => x.itemcode === row.itemcode && x.warehouse_id === wh);
        return Number(inv?.onhand_qty || 0);
      }
    },
    {
      title: 'Issue Qty', key: 'issue_qty', width: 180, align: 'right',
      render: (_, row) => {
        const wh = fromWarehouseId;
        const inv = (wh == null) ? undefined : (inventory || []).find(x => x.itemcode === row.itemcode && x.warehouse_id === wh);
        const onhand = Number(inv?.onhand_qty || 0);
        const maxQty = Math.min(onhand, Number(row.outstanding_qty || 0));
        const val = issueQtyByItem[row.item_id] ?? 0;
        return (
          <Space>
            <InputNumber
              min={0}
              value={val}
              onChange={(v) => setIssueQtyByItem(prev => ({ ...prev, [row.item_id]: typeof v === 'number' ? v : 0 }))}
              style={{ width: 120 }}
              disabled={!canTransact}
            />
            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>max {maxQty}</Text>
          </Space>
        );
      }
    },
    {
      title: 'Actions', key: 'actions', width: 120,
      render: (_, row) => {
        const wh = fromWarehouseId;
        const inv = (wh == null) ? undefined : (inventory || []).find(x => x.itemcode === row.itemcode && x.warehouse_id === wh);
        const onhand = Number(inv?.onhand_qty || 0);
        const maxQty = Math.min(onhand, Number(row.outstanding_qty || 0));
        return (
          <Space>
            <Button size="small" onClick={() => setIssueQtyByItem(prev => ({ ...prev, [row.item_id]: maxQty }))} disabled={!canTransact}>All</Button>
          </Space>
        );
      }
    }
  ], [issueQtyByItem, inventory, whMap, fromWarehouseId, canTransact]);

  const handleFulfillSubmit = async () => {
    try {
      if (!selectedRefNo) { message.warning('Select a request to fulfill'); return; }
      if (!canTransact) { message.error('You are not authorized to issue from this warehouse.'); return; }
      if (fromWarehouseId == null) { message.warning('Select From Warehouse'); return; }
      if (!issueDate) { message.warning('Select Issue Date'); return; }
      if (!personnelCode) { message.warning('Please select personnel'); return; }

      const payloads: any[] = [];
      for (const item of (visibleItems || [])) {
        const qty = Number(issueQtyByItem[item.item_id] || 0);
        if (!qty || qty <= 0) continue;
        const whId = fromWarehouseId;
        if (whId == null) { message.error(`Select a warehouse for item ${item.item_id}`); return; }
        const inv = (inventory || []).find(x => x.itemcode === item.itemcode && x.warehouse_id === whId);
        const onhand = Number(inv?.onhand_qty || 0);
        const maxOutstanding = Number(item.outstanding_qty || 0);
        if (qty > onhand) { message.error(`Qty exceeds on-hand for item ${item.itemcode} (WH ${whId})`); return; }
        if (qty > maxOutstanding) { message.error(`Qty exceeds outstanding for item ${item.item_id}`); return; }
        payloads.push({
          op_type: 'issue' as const,
          itemcode: item.itemcode,
          qty,
          unit: item.unit || (inv as any)?.unit || 'EA',
          from_warehouse_id: whId,
          txn_date: issueDate || undefined,
          request_ref_no: selectedRefNo,
          issued_to: personnelCode || null,
        });
      }
      if (payloads.length === 0) { message.warning('Enter allocations to issue'); return; }

      const seq = await getNextSequenceValue('mat_clnt_txns_issue_seq');
      const refNo = `ISS-${String(seq || 0).padStart(7, '0')}`;
      payloads.forEach(p => p.reference_no = refNo);

      await insertData('mat_clnt_txns', payloads);
      message.success(`Issued ${payloads.length} lines. Ref ${refNo}`);
      setIssueQtyByItem({});

      if (typeof refetchInventory === 'function') {
        try { await refetchInventory(); } catch {}
      }

      let freshOutstanding: any = null;
      if (typeof refetchOutstanding === 'function') {
        try { freshOutstanding = await refetchOutstanding(); } catch {}
      }
      if (typeof refetchReqHeaders === 'function') { await refetchReqHeaders(); }

      try {
        const latestAll: VwMatClntRequestItemOutstanding[] = Array.isArray(freshOutstanding?.data)
          ? freshOutstanding.data
          : (Array.isArray(reqOutData) ? (reqOutData as any) : (reqOutData?.data ?? []));
        const latestForRef = (latestAll || []).filter(r => String(r.ref_no || '').trim() === String(selectedRefNo || '').trim());
        const anyOutstanding = latestForRef.some(r => Number(r.outstanding_qty || 0) > 0);
        const nextStatus = anyOutstanding ? 'partially_issued' : 'issued';
        await updateData('mat_clnt_issue_requests', String(selectedRefNo || ''), { status: nextStatus }, 'ref_no');
      } catch {}

      navigate(-1);
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || 'Failed to issue');
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Client Materials - Fulfill Issue Request</Title>
              <Text type="secondary">Allocate quantities for the selected warehouse against a client request</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Select
              placeholder="From Warehouse"
              allowClear={false}
              value={fromWarehouseId}
              onChange={(v) => setFromWarehouseId(v as number)}
              options={fromWarehouseOptions}
              style={{ width: 240 }}
              showSearch
              optionFilterProp="label"
            />
            <Select
              placeholder="Approved/Partial Requests"
              allowClear
              showSearch
              value={selectedRefNo}
              onChange={(v) => setSelectedRefNo(v as (string | undefined))}
              options={requestOptions}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              style={{ width: 340 }}
              disabled={!fromWarehouseId}
            />
            <span>
              <Text strong>Issue Date</Text>{' '}
              <DatePicker value={issueDate ? dayjs(issueDate) : null} onChange={d => setIssueDate(d ? d.format('YYYY-MM-DD') : null)} style={{ width: 160 }} />
            </span>
            <Input
              placeholder="Issued to Personnel"
              value={personnelDisplay}
              onChange={(e) => setPersonnelDisplay(e.target.value)}
              style={{ width: 260 }}
              suffix={<Button size="small" onClick={() => setPersonnelOpen(true)}>Select</Button>}
            />
            <Button action="add" type="primary" onClick={handleFulfillSubmit} disabled={!canTransact}>Submit Issue</Button>
          </Space>
        </Col>
      </Row>

      <PersonnelSelect
        open={personnelOpen}
        mode="both"
        onCancel={() => setPersonnelOpen(false)}
        onOk={(val: any) => {
          try {
            if (Array.isArray(val)) {
              const [, codeStr, fullName] = val as [string, string, string];
              setPersonnelCode(codeStr ?? null);
              setPersonnelDisplay(fullName ?? '');
            } else {
              const str = String(val);
              const parts = str.split(' - ');
              const codeStr = parts[0] ?? '';
              const fullName = parts.slice(1).join(' - ') || '';
              setPersonnelCode(codeStr || null);
              setPersonnelDisplay(fullName);
            }
          } catch {
            setPersonnelCode(null);
          }
          setPersonnelOpen(false);
        }}
      />

      {selectedRefNo ? (
        <div style={{ marginBottom: 8 }}>
          <Alert
            type={canTransact ? 'info' : 'warning'}
            showIcon
            message={canTransact
              ? 'Only items requested from the selected warehouse are shown. Issue per warehouse if the request spans multiple warehouses.'
              : 'You are not an owner of the selected warehouse. Issuing transactions are restricted.'}
          />
        </div>
      ) : null}

      {!selectedRefNo ? (
        <Card size="small"><Text type="secondary">Select a request to start fulfillment.</Text></Card>
      ) : (
        <Table<VwMatClntRequestItemOutstanding>
          columns={fulfillColumns}
          dataSource={visibleItems}
          rowKey={(r) => String(r.item_id)}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      )}
    </Card>
  );
}
